import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { playCopy, playSuccess, playClick } from '../utils/SoundManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ReferralPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);

  const getHeaders = () => {
    const uid = localStorage.getItem('userId');
    return uid ? { 'X-User-Id': uid } : {};
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [codeRes, refRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/referral/my-code`, { withCredentials: true, headers: getHeaders() }),
        axios.get(`${BACKEND_URL}/api/referral/my-referrals`, { withCredentials: true, headers: getHeaders() }),
      ]);
      setData(codeRes.data);
      setReferrals(refRes.data.referrals || []);
    } catch (err) {
      console.error('Failed to load referral data', err);
    } finally {
      setLoading(false);
    }
  };

  const referralLink = data ? `${window.location.origin}/?ref=${data.referral_code}` : '';
  const shareMessage = data
    ? `🌹 انضم إليّ في وِداد - منصة الزواج الشرعي الأولى!\n\nاستخدم كود الدعوة: ${data.referral_code}\nواحصل على ${data.reward_new_user_days} أيام عضوية مميزة مجاناً 🎁\n\n${referralLink}`
    : '';

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    playCopy();
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const shareWhatsApp = () => {
    playClick();
    const url = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, '_blank');
  };

  const shareTwitter = () => {
    playClick();
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, '_blank');
  };

  const shareNative = async () => {
    playClick();
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'وِداد - منصة الزواج الشرعي',
          text: shareMessage,
          url: referralLink,
        });
        playSuccess();
      } catch (err) {
        // user cancelled
      }
    } else {
      copyToClipboard(shareMessage, 'message');
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--light-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="floating" style={{ fontSize: '40px' }}>🎁</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--light-bg)' }}>
      <div className="header">
        <div className="header-content">
          <h1 className="logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>وِداد</h1>
          <button className="nav-link" onClick={() => navigate('/dashboard')} data-testid="referral-back-btn">
            ← الرئيسية
          </button>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '30px', paddingBottom: '60px', maxWidth: '720px' }}>
        
        {/* Hero */}
        <div className="card fade-in" style={{ 
          padding: '32px 24px', 
          borderRadius: '20px',
          textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(255, 107, 157, 0.08), rgba(192, 110, 237, 0.08))',
          border: '1px solid rgba(255, 107, 157, 0.18)',
          marginBottom: '20px'
        }}>
          <div className="floating" style={{ fontSize: '64px', marginBottom: '8px' }}>🎁</div>
          <h1 style={{ fontSize: '26px', color: 'var(--text-dark)', marginBottom: '10px' }}>
            ادعُ أصدقاءك واكسب Premium مجاناً
          </h1>
          <p style={{ color: 'var(--text-light)', fontSize: '15px', lineHeight: '1.9', margin: 0 }}>
            عن كل صديق ينضم بكودك: تحصل على <strong style={{ color: 'var(--primary)' }}>{data?.reward_referrer_days || 7} أيام</strong> Premium،
            ويحصل صديقك على <strong style={{ color: 'var(--purple)' }}>{data?.reward_new_user_days || 3} أيام</strong> Premium 💎
          </p>
        </div>

        {/* Stats */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '12px', 
          marginBottom: '20px' 
        }}>
          <div className="card" data-testid="stat-friends-card" style={{ padding: '20px', textAlign: 'center', borderRadius: '16px' }}>
            <div style={{ fontSize: '32px', marginBottom: '6px' }}>👥</div>
            <div style={{ fontSize: '28px', fontWeight: '800', color: 'var(--primary)' }}>
              {data?.referral_count || 0}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>صديق انضم</div>
          </div>
          <div className="card" data-testid="stat-days-card" style={{ padding: '20px', textAlign: 'center', borderRadius: '16px' }}>
            <div style={{ fontSize: '32px', marginBottom: '6px' }}>💎</div>
            <div style={{ fontSize: '28px', fontWeight: '800', color: 'var(--purple)' }}>
              {data?.days_earned || 0}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>يوم Premium مكتسبة</div>
          </div>
        </div>

        {/* Code Box */}
        <div className="card" style={{ padding: '24px', borderRadius: '20px', marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-light)', marginBottom: '10px', fontWeight: '600' }}>
            🔑 كود الدعوة الخاص بك
          </label>
          <div 
            data-testid="referral-code-box"
            onClick={() => copyToClipboard(data?.referral_code, 'code')}
            style={{
              background: 'linear-gradient(135deg, var(--primary), var(--purple))',
              color: 'white',
              padding: '20px',
              borderRadius: '14px',
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: '800',
              letterSpacing: '2px',
              cursor: 'pointer',
              fontFamily: 'monospace',
              direction: 'ltr',
              boxShadow: '0 6px 20px rgba(255, 107, 157, 0.35)',
              transition: 'transform 0.2s ease',
              userSelect: 'all'
            }}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            {data?.referral_code || '...'}
          </div>
          <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '12px', color: copiedField === 'code' ? 'var(--success)' : 'var(--text-light)' }}>
            {copiedField === 'code' ? '✓ تم النسخ!' : '👆 اضغط لنسخ الكود'}
          </div>
        </div>

        {/* Link Box */}
        <div className="card" style={{ padding: '20px', borderRadius: '16px', marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-light)', marginBottom: '10px', fontWeight: '600' }}>
            🔗 رابط الدعوة المباشر
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div 
              data-testid="referral-link-box"
              style={{ 
                flex: 1, 
                background: 'var(--light-bg)', 
                padding: '12px 14px', 
                borderRadius: '10px',
                fontSize: '13px',
                color: 'var(--text-dark)',
                direction: 'ltr',
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                border: '1px solid var(--border-color)'
              }}
            >
              {referralLink}
            </div>
            <button 
              data-testid="copy-link-btn"
              className="btn btn-outline"
              onClick={() => copyToClipboard(referralLink, 'link')}
              style={{ padding: '12px 16px', fontSize: '13px', flexShrink: 0 }}
            >
              {copiedField === 'link' ? '✓' : '📋 نسخ'}
            </button>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="card" style={{ padding: '20px', borderRadius: '16px', marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-light)', marginBottom: '14px', fontWeight: '600', textAlign: 'center' }}>
            📤 شارك مع أصدقائك
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
            <button 
              data-testid="share-whatsapp-btn"
              onClick={shareWhatsApp}
              style={{
                padding: '14px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, #25D366, #128C7E)', color: 'white',
                fontSize: '13px', fontWeight: '700', display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: '4px', transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <span style={{ fontSize: '24px' }}>💬</span>
              <span>واتساب</span>
            </button>
            <button 
              data-testid="share-twitter-btn"
              onClick={shareTwitter}
              style={{
                padding: '14px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, #1DA1F2, #0E76B8)', color: 'white',
                fontSize: '13px', fontWeight: '700', display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: '4px', transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <span style={{ fontSize: '24px' }}>🐦</span>
              <span>تويتر/X</span>
            </button>
            <button 
              data-testid="share-native-btn"
              onClick={shareNative}
              style={{
                padding: '14px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, var(--primary), var(--purple))', color: 'white',
                fontSize: '13px', fontWeight: '700', display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: '4px', transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <span style={{ fontSize: '24px' }}>📲</span>
              <span>مشاركة</span>
            </button>
          </div>
        </div>

        {/* Referrals List */}
        {referrals.length > 0 && (
          <div className="card" style={{ padding: '20px', borderRadius: '16px', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', color: 'var(--text-dark)', marginBottom: '14px' }}>
              🌟 الأصدقاء الذين انضموا بدعوتك
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {referrals.map((r, idx) => (
                <div key={idx} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '12px 14px',
                  background: 'var(--light-bg)',
                  borderRadius: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ 
                      width: '36px', height: '36px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, var(--primary), var(--purple))',
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: '700', fontSize: '15px'
                    }}>
                      {r.name?.charAt(0) || '👤'}
                    </div>
                    <span style={{ color: 'var(--text-dark)', fontSize: '14px', fontWeight: '600' }}>
                      {r.name || 'عضو جديد'}
                    </span>
                  </div>
                  <span style={{ color: 'var(--success)', fontSize: '12px', fontWeight: '700' }}>
                    +7 أيام 💎
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How it works */}
        <div className="card" style={{ padding: '20px', borderRadius: '16px', background: 'rgba(0, 137, 123, 0.05)' }}>
          <h3 style={{ fontSize: '16px', color: 'var(--text-dark)', marginBottom: '14px', textAlign: 'center' }}>
            ✨ كيف يعمل النظام؟
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { icon: '1️⃣', text: 'شارك كودك أو رابطك مع صديقك' },
              { icon: '2️⃣', text: 'صديقك يسجّل في وِداد بالكود' },
              { icon: '3️⃣', text: 'تحصل على 7 أيام Premium، وصديقك على 3 أيام 🎉' },
            ].map((step, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', color: 'var(--text-dark)' }}>
                <span style={{ fontSize: '22px' }}>{step.icon}</span>
                <span>{step.text}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

export default ReferralPage;
